import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, Search, Phone, Mail, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { AddressLink } from '../../components/MapsNavProvider';
import FieldCustomerDetail from '../FieldCustomerDetail';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeeCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', notes: '', send_invite: false });
  const [search, setSearch] = useState('');
  const [dupes, setDupes] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(data.filter(c => !c.merged_into));
    } catch (err) { console.error(err); }
  };

  // Duplicate detection on phone/email/address/name change (fuzzy)
  useEffect(() => {
    if (!showForm) { setDupes([]); return; }
    const t = setTimeout(async () => {
      if (!form.phone && !form.email && !form.address && (!form.name || form.name.length < 3)) { setDupes([]); return; }
      try {
        const params = new URLSearchParams();
        if (form.phone) params.set('phone', form.phone);
        if (form.email) params.set('email', form.email);
        if (form.address) params.set('address', form.address);
        if (form.name) params.set('name', form.name);
        const { data } = await axios.get(`${API}/customers/check-duplicate-v2?${params}`, { withCredentials: true });
        setDupes(data.matches || []);
      } catch { setDupes([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.phone, form.email, form.address, form.name, showForm]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/customers`, form, { withCredentials: true });
      if (data.existing) toast.info('Customer already exists');
      else if (form.send_invite && data.temp_password) toast.success(`Created. Temp password: ${data.temp_password}`);
      else toast.success('Customer created');
      setForm({ name: '', email: '', phone: '', address: '', notes: '', send_invite: false });
      setShowForm(false);
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const filtered = search ? customers.filter(c => `${c.name} ${c.email} ${c.phone} ${c.address}`.toLowerCase().includes(search.toLowerCase())) : customers;

  if (selected) {
    return (
      <div data-testid="employee-customers-page" className="space-y-6">
        <FieldCustomerDetail customer={selected} onBack={() => setSelected(null)} />
      </div>
    );
  }

  return (
    <div data-testid="employee-customers-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1">Customers</h2>
          <p className="text-[#A3A3A3]">Look up or add a new customer in the field</p>
        </div>
        <Button data-testid="emp-new-customer-btn" onClick={() => setShowForm(!showForm)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-2" /> New Customer</Button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="emp-customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone, email, address" className="pl-10 bg-[#0A0A0A] border-[#262626] text-white" />
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-[#141414] border border-[#262626] rounded-md p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="text-white">Name *</Label><Input data-testid="emp-cust-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
          <div><Label className="text-white">Phone</Label><Input data-testid="emp-cust-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
          <div><Label className="text-white">Email</Label><Input data-testid="emp-cust-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
          <div><Label className="text-white">Address</Label><Input data-testid="emp-cust-address" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
          <div className="md:col-span-2"><Label className="text-white">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>

          {dupes.length > 0 && (
            <div data-testid="dup-warning" className="md:col-span-2 bg-[#1a0e0e] border border-red-700/40 rounded-md p-3">
              <p className="text-red-400 text-xs uppercase font-bold flex items-center gap-2"><AlertTriangle className="w-3 h-3" /> Possible duplicate(s) found:</p>
              {dupes.map(d => <p key={d.id} className="text-white text-sm mt-1">• {d.name} ({d.match}{d.score ? `, ${Math.round(d.score*100)}% match` : ''}) — {d.email || d.phone}</p>)}
            </div>
          )}

          <label className="flex items-center gap-2 text-white text-sm md:col-span-2">
            <input data-testid="emp-cust-invite" type="checkbox" checked={form.send_invite} onChange={(e) => setForm({...form, send_invite: e.target.checked})} className="accent-[#D4AF37]" />
            Invite customer to mobile app
          </label>
          <Button data-testid="emp-cust-submit" type="submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] md:col-span-2">Create Customer</Button>
        </form>
      )}

      <div className="grid gap-2">
        {filtered.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-6 text-center text-[#A3A3A3]">No customers found</div>
        ) : filtered.map(c => (
          <div key={c.id} data-testid={`emp-customer-row-${c.id}`} onClick={() => setSelected(c)} className="bg-[#141414] border border-[#262626] rounded-md p-3 flex items-center gap-3 cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
            <Users className="w-4 h-4 text-[#D4AF37]" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{c.name}</p>
              <div className="flex gap-x-3 gap-y-1 flex-wrap text-[#A3A3A3] text-xs mt-0.5">
                {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                {c.address && (
                  <span onClick={(e) => e.stopPropagation()}>
                    <AddressLink
                      address={c.address}
                      testId={`emp-cust-address-link-${c.id}`}
                      className="text-[#A3A3A3] text-xs"
                      iconClassName="w-3 h-3"
                    />
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmployeeCustomers;
